import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CommunityDocuments = () => {
  const [activeTab, setActiveTab] = useState('SYLLABUS');
  const [documents, setDocuments] = useState([]);
  const [feeDocuments, setFeeDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newDocument, setNewDocument] = useState({
    id: null,
    type: 'SYLLABUS',
    pdf: null,
    thumbnail: null,
    pdf_url: null,
    thumbnail_url: null,
    school_name: '', // For fee structure
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const baseURL = 'https://apissc.sainikschoolcadet.com/api/documents';
  const feeBaseURL = 'https://apissc.sainikschoolcadet.com/api/SchoolFeePdf';

  useEffect(() => {
    if (activeTab === 'FEE_STRUCTURE') {
      fetchFeeDocuments();
    } else {
      fetchDocuments();
    }
    setNewDocument(prev => ({ ...prev, type: activeTab }));
  }, [activeTab]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/type/${activeTab}`);
      setDocuments(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(feeBaseURL);
      setFeeDocuments(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch fee documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        if (activeTab === 'FEE_STRUCTURE') {
          await axios.delete(`${feeBaseURL}/${id}`);
          setFeeDocuments(feeDocuments.filter(doc => doc.id !== id));
        } else {
          await axios.delete(`${baseURL}/${id}`);
          setDocuments(documents.filter(doc => doc.id !== id));
        }
      } catch (err) {
        setError('Failed to delete document');
      }
    }
  };

  const handleViewPdf = (pdfUrl) => window.open(pdfUrl, '_blank');

  const handleFileChange = (e) => {
    setNewDocument({
      ...newDocument,
      [e.target.name]: e.target.files[0],
    });
  };

  const handleInputChange = (e) => {
    setNewDocument({
      ...newDocument,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEditing && !newDocument.pdf) {
      setError('Please select a PDF file');
      return;
    }

    if (activeTab === 'FEE_STRUCTURE' && !newDocument.school_name) {
      setError('Please enter school name');
      return;
    }
    
    const formData = new FormData();
    
    if (activeTab === 'FEE_STRUCTURE') {
      formData.append('school_name', newDocument.school_name);
      if (newDocument.pdf) {
        formData.append('pdf', newDocument.pdf);
      }
    } else {
      formData.append('type', newDocument.type);
      if (newDocument.pdf) {
        formData.append('pdf', newDocument.pdf);
      }
      if (newDocument.thumbnail) {
        formData.append('thumbnail', newDocument.thumbnail);
      }
    }
    
    setIsUploading(true);
    
    try {
      if (isEditing) {
        if (activeTab === 'FEE_STRUCTURE') {
          await axios.put(`${feeBaseURL}/${newDocument.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          await axios.put(`${baseURL}/${newDocument.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      } else {
        if (activeTab === 'FEE_STRUCTURE') {
          await axios.post(feeBaseURL, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          await axios.post(baseURL, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      
      resetForm();
      
      setUploadSuccess(true);
      setTimeout(() => {
        setUploadSuccess(false);
        setShowModal(false);
        setIsEditing(false);
      }, 2000);
      
      if (activeTab === 'FEE_STRUCTURE') {
        fetchFeeDocuments();
      } else {
        fetchDocuments();
      }
    } catch (err) {
      setError(`Failed to ${isEditing ? 'update' : 'upload'} document`);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setNewDocument({ 
      id: null,
      type: activeTab, 
      pdf: null, 
      thumbnail: null,
      pdf_url: null,
      thumbnail_url: null,
      school_name: ''
    });
    
    const pdfInput = document.getElementById('pdf-upload');
    const thumbnailInput = document.getElementById('thumbnail-upload');
    if (pdfInput) pdfInput.value = '';
    if (thumbnailInput) thumbnailInput.value = '';
  };

  const openAddModal = () => {
    resetForm();
    setError(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (document) => {
    if (activeTab === 'FEE_STRUCTURE') {
      setNewDocument({
        id: document.id,
        type: activeTab,
        pdf: null,
        thumbnail: null,
        pdf_url: document.file_url,
        thumbnail_url: null,
        school_name: document.school_name
      });
    } else {
      setNewDocument({
        id: document.id,
        type: document.type,
        pdf: null,
        thumbnail: null,
        pdf_url: document.pdf_url,
        thumbnail_url: document.thumbnail_url,
        school_name: ''
      });
    }
    setError(null);
    setIsEditing(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError(null);
    setIsEditing(false);
  };

  const currentDocuments = activeTab === 'FEE_STRUCTURE' ? feeDocuments : documents;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6">Community Documents</h1>
      
      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
          <li className="mr-2">
            <button
              className={`inline-block p-4 border-b-2 rounded-t-lg ${
                activeTab === 'SYLLABUS' ? 'border-blue-600 text-blue-600' : 'border-transparent hover:text-gray-600'
              }`}
              onClick={() => setActiveTab('SYLLABUS')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Syllabus
              </div>
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 border-b-2 rounded-t-lg ${
                activeTab === 'MAGAZINE' ? 'border-blue-600 text-blue-600' : 'border-transparent hover:text-gray-600'
              }`}
              onClick={() => setActiveTab('MAGAZINE')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                Magazine
              </div>
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 border-b-2 rounded-t-lg ${
                activeTab === 'CURRENT_AFFAIRS' ? 'border-blue-600 text-blue-600' : 'border-transparent hover:text-gray-600'
              }`}
              onClick={() => setActiveTab('CURRENT_AFFAIRS')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                </svg>
                Current Affairs
              </div>
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 border-b-2 rounded-t-lg ${
                activeTab === 'FEE_STRUCTURE' ? 'border-blue-600 text-blue-600' : 'border-transparent hover:text-gray-600'
              }`}
              onClick={() => setActiveTab('FEE_STRUCTURE')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                School Fee Structure
              </div>
            </button>
          </li>
        </ul>
      </div>
      
      {/* Documents Table with Add Button */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            {activeTab === 'SYLLABUS' ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Syllabus Documents
              </>
            ) : activeTab === 'MAGAZINE' ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                Magazine Documents
              </>
            ) : activeTab === 'CURRENT_AFFAIRS' ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                </svg>
                Current Affairs Documents
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                School Fee Structure
              </>
            )}
          </h2>
          <button
            onClick={openAddModal}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-2 text-gray-600">Loading documents...</p>
          </div>
        ) : currentDocuments.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-gray-500">No {activeTab === 'FEE_STRUCTURE' ? 'fee structure' : activeTab === 'CURRENT_AFFAIRS' ? 'current affairs' : activeTab.toLowerCase()} documents found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  {activeTab === 'FEE_STRUCTURE' ? (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School Name</th>
                  ) : (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thumbnail</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentDocuments.map((doc, index) => (
                  <tr key={doc.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{doc.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {activeTab === 'FEE_STRUCTURE' ? (
                        <span className="text-sm text-gray-900">{doc.school_name}</span>
                      ) : doc.thumbnail_url ? (
                        <img src={doc.thumbnail_url} alt="Thumbnail" className="h-16 w-auto object-cover rounded" />
                      ) : (
                        <div className="h-16 w-16 bg-gray-100 flex items-center justify-center rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center w-fit ${
                        activeTab === 'FEE_STRUCTURE' ? 'bg-orange-100 text-orange-800' :
                        activeTab === 'CURRENT_AFFAIRS' ? 'bg-red-100 text-red-800' :
                        doc.type === 'SYLLABUS' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {activeTab === 'FEE_STRUCTURE' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : activeTab === 'CURRENT_AFFAIRS' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                          </svg>
                        ) : doc.type === 'SYLLABUS' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                        )}
                        {activeTab === 'FEE_STRUCTURE' ? 'FEE STRUCTURE' : activeTab === 'CURRENT_AFFAIRS' ? 'CURRENT AFFAIRS' : doc.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 space-x-4">
                      <button 
                        onClick={() => handleViewPdf(activeTab === 'FEE_STRUCTURE' ? doc.file_url : doc.pdf_url)} 
                        className="text-blue-600 hover:text-blue-800" 
                        title="View PDF"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => openEditModal(doc)} 
                        className="text-blue-600 hover:text-blue-800" 
                        title="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDelete(doc.id)} 
                        className="text-red-600 hover:text-red-800" 
                        title="Delete"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Add/Edit Document Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 md:mx-0">
            <div className="p-6">
              <h3 className="text-xl font-semibold mb-4">
                {isEditing ? 'Edit' : 'Add New'} {
                  activeTab === 'FEE_STRUCTURE' ? 'Fee Structure' :
                  activeTab === 'CURRENT_AFFAIRS' ? 'Current Affairs' :
                  activeTab === 'SYLLABUS' ? 'Syllabus' : 'Magazine'
                }
              </h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )}
              {uploadSuccess && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 rounded flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Document {isEditing ? 'updated' : 'uploaded'} successfully!
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Document Type *
                  </label>
                  <input
                    type="text"
                    value={
                      activeTab === 'FEE_STRUCTURE' ? 'Fee Structure' :
                      activeTab === 'CURRENT_AFFAIRS' ? 'Current Affairs' :
                      activeTab === 'SYLLABUS' ? 'Syllabus' : 'Magazine'
                    }
                    className="w-full rounded-md border-gray-300 bg-gray-100 shadow-sm cursor-not-allowed px-3 py-2"
                    disabled
                  />
                </div>

                {activeTab === 'FEE_STRUCTURE' && (
                  <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      School Name *
                    </label>
                    <input
                      type="text"
                      name="school_name"
                      value={newDocument.school_name}
                      onChange={handleInputChange}
                      placeholder="Enter school name"
                      className="w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    PDF Document {!isEditing && '*'}
                  </label>
                  {isEditing && newDocument.pdf_url && (
                    <div className="mb-2 text-sm text-blue-600 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Current PDF file is available</span>
                    </div>
                  )}
                  <input
                    id="pdf-upload"
                    type="file"
                    name="pdf"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required={!isEditing}
                  />
                  {isEditing && (
                    <p className="mt-1 text-xs text-gray-500">Upload a new PDF to replace the current one (optional)</p>
                  )}
                </div>
                
                {activeTab !== 'FEE_STRUCTURE' && (
                  <div className="mb-6">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Thumbnail Image
                    </label>
                    {isEditing && newDocument.thumbnail_url && (
                      <div className="mb-2">
                        <img 
                          src={newDocument.thumbnail_url} 
                          alt="Current thumbnail" 
                          className="h-16 w-auto object-cover rounded mb-2" 
                        />
                      </div>
                    )}
                    <input
                      id="thumbnail-upload"
                      type="file"
                      name="thumbnail"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    {isEditing && (
                      <p className="mt-1 text-xs text-gray-500">Upload a new thumbnail to replace the current one (optional)</p>
                    )}
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none disabled:opacity-50 flex items-center"
                  >
                    {isUploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditing ? 'Updating...' : 'Uploading...'}
                      </>
                    ) : isEditing ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityDocuments;